import React, { useEffect, useMemo, useRef, useState } from "react";
import { loadBranchWaiterAssistance, saveBranchWaiterAssistance } from "../../services/supabase/waiterAssistanceService";
import TabletOrderLauncher from "../common/TabletOrderLauncher";
import { useAuth } from "../context/AuthContext";
import {
  cancelInvitation,
  createWaiterInvitation,
  deactivateCompanyUser,
  listBranchWaiters,
  listCompanyInvitations,
  updateWaiterBranchAccess,
} from "../../services/supabase/companyAdminService";
import {
  buildWaiterAssistanceStorageKey,
  getWaiterAssistanceConfig,
  saveWaiterAssistanceConfig,
  WAITER_ASSISTANCE_UPDATED_EVENT,
} from "../../services/local/waiterAssistance";
import {
  buildInvitationRegistrationUrl,
  copyTextToClipboard,
} from "../../services/local/invitationLinks";
import { syncBranchActiveWaiters } from "../../services/local/waiterOperations";
import {
  DEFAULT_WAITER_PERMISSIONS,
  normalizeWaiterPermissions,
  WAITER_PERMISSION_CONFIG,
} from "../../services/local/waiterPermissions";

const createInitialWaiterForm = () => ({
  fullName: "",
  email: "",
  waiterPermissions: { ...DEFAULT_WAITER_PERMISSIONS },
});

const buildWaiterPermissionSummary = (waiterPermissions = {}) => {
  const normalized = normalizeWaiterPermissions(waiterPermissions, DEFAULT_WAITER_PERMISSIONS);
  const activeLabels = WAITER_PERMISSION_CONFIG.filter(
    (permission) => normalized[permission.key]
  ).map((permission) => permission.label);

  return activeLabels.length > 0 ? activeLabels.join(" · ") : "Sin permisos operativos.";
};

const createOptionDraft = (label = "") => ({
  id: `assist-${Date.now()}-${Math.random().toString(16).slice(2, 7)}`,
  label,
  active: true,
});

function MozoAdmin() {
  const {
    currentRestaurant,
    currentCompany,
    currentBranch,
    user,
    isCreatorMode,
    isSupabaseConfigured,
  } = useAuth();
  const [estadoMozos, setEstadoMozos] = useState("");
  const [estadoOpciones, setEstadoOpciones] = useState("");
  const [waiterForm, setWaiterForm] = useState(createInitialWaiterForm);
  const [branchWaiters, setBranchWaiters] = useState([]);
  const [branchInvitations, setBranchInvitations] = useState([]);
  const [loadingWaiters, setLoadingWaiters] = useState(false);
  const [assistanceConfig, setAssistanceConfig] = useState(() =>
    getWaiterAssistanceConfig(currentBranch?.id || currentRestaurant?.id)
  );
  const [newOptionLabel, setNewOptionLabel] = useState("");
  const [expandedWaiterId, setExpandedWaiterId] = useState("");
  const [waiterPermissionDrafts, setWaiterPermissionDrafts] = useState({});
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [savingOptions, setSavingOptions] = useState(false);
  const [optionsLoadFailed, setOptionsLoadFailed] = useState(false);
  const [optionsReload, setOptionsReload] = useState(0);
  const optionsScopeId = currentBranch?.id || currentRestaurant?.id;
  const optionsScopeRef = useRef(optionsScopeId);
  optionsScopeRef.current = optionsScopeId;

  useEffect(() => {
    let active = true;
    setAssistanceConfig(getWaiterAssistanceConfig(optionsScopeId));
    setEstadoOpciones("");
    setEstadoMozos("");
    setSavingOptions(false);
    setOptionsLoadFailed(false);
    if (isCreatorMode || !isSupabaseConfigured) {
      setLoadingOptions(false);
      return undefined;
    }
    setLoadingOptions(true);
    loadBranchWaiterAssistance(optionsScopeId).then((config) => {
      if (!active) return;
      if (config) setAssistanceConfig(config);
      else setEstadoOpciones("Guarda estas opciones para publicarlas en el menu QR de la sucursal.");
    }).catch((error) => {
      if (!active) return;
      setOptionsLoadFailed(true);
      setEstadoOpciones(error.message);
    }).finally(() => {
      if (active) setLoadingOptions(false);
    });
    return () => { active = false; };
  }, [optionsScopeId, isCreatorMode, isSupabaseConfigured, optionsReload]);

  useEffect(() => {
    const scopeId = currentBranch?.id || currentRestaurant?.id || "default";

    const handleUpdate = (event) => {
      if ((event.detail?.scopeId || "default") !== scopeId) {
        return;
      }

      setAssistanceConfig(event.detail?.config || getWaiterAssistanceConfig(scopeId));
    };

    const handleStorage = (event) => {
      if (event.key !== buildWaiterAssistanceStorageKey(scopeId)) {
        return;
      }

      setAssistanceConfig(getWaiterAssistanceConfig(scopeId));
    };

    window.addEventListener(WAITER_ASSISTANCE_UPDATED_EVENT, handleUpdate);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(WAITER_ASSISTANCE_UPDATED_EVENT, handleUpdate);
      window.removeEventListener("storage", handleStorage);
    };
  }, [currentBranch?.id, currentRestaurant?.id]);

  useEffect(() => {
    let active = true;

    const loadWaiters = async () => {
      if (
        isCreatorMode ||
        !isSupabaseConfigured ||
        !currentCompany?.id ||
        !currentRestaurant?.id
      ) {
        if (active) {
          setBranchWaiters([]);
          setBranchInvitations([]);
          if (currentRestaurant?.id) {
            syncBranchActiveWaiters({ id: currentRestaurant.id }, currentRestaurant.id, []);
          }
        }
        return;
      }

      setLoadingWaiters(true);
      setEstadoMozos("");

      try {
        const [nextWaiters, nextInvitations] = await Promise.all([
          listBranchWaiters(currentCompany.id, currentRestaurant.id),
          listCompanyInvitations(currentCompany.id),
        ]);

        if (!active) {
          return;
        }

        setBranchWaiters(nextWaiters);
        syncBranchActiveWaiters(
          { id: currentRestaurant.id },
          currentRestaurant.id,
          nextWaiters.filter((waiter) => waiter.branchAccessActive)
        );
        setBranchInvitations(
          nextInvitations.filter(
            (invitation) =>
              invitation.branch_id === currentRestaurant.id &&
              invitation.workspace_role === "mozo"
          )
        );
      } catch (error) {
        if (!active) {
          return;
        }

        setEstadoMozos(error.message || "No se pudo cargar el equipo de salon.");
      } finally {
        if (active) {
          setLoadingWaiters(false);
        }
      }
    };

    loadWaiters();

    return () => {
      active = false;
    };
  }, [
    currentCompany?.id,
    currentRestaurant?.id,
    isCreatorMode,
    isSupabaseConfigured,
  ]);

  useEffect(() => {
    setWaiterPermissionDrafts((prev) =>
      branchWaiters.reduce((acc, waiter) => {
        acc[waiter.userId] =
          prev[waiter.userId] ||
          normalizeWaiterPermissions(waiter.waiterPermissions, DEFAULT_WAITER_PERMISSIONS);
        return acc;
      }, {})
    );
  }, [branchWaiters]);

  const pendingWaiterInvitations = useMemo(
    () => branchInvitations.filter((invitation) => invitation.status === "pending"),
    [branchInvitations]
  );

  const activeWaitersCount = useMemo(
    () => branchWaiters.filter((waiter) => waiter.branchAccessActive).length,
    [branchWaiters]
  );

  const reloadWaiters = async () => {
    if (
      isCreatorMode ||
      !isSupabaseConfigured ||
      !currentCompany?.id ||
      !currentRestaurant?.id
    ) {
      setBranchWaiters([]);
      setBranchInvitations([]);
      return;
    }

    const [nextWaiters, nextInvitations] = await Promise.all([
      listBranchWaiters(currentCompany.id, currentRestaurant.id),
      listCompanyInvitations(currentCompany.id),
    ]);

  setBranchWaiters(nextWaiters);
  syncBranchActiveWaiters(
    { id: currentRestaurant.id },
    currentRestaurant.id,
    nextWaiters.filter((waiter) => waiter.branchAccessActive)
  );
    setBranchInvitations(
      nextInvitations.filter(
        (invitation) =>
          invitation.branch_id === currentRestaurant.id &&
          invitation.workspace_role === "mozo"
      )
    );
  };

  const handleCreateWaiter = async (event) => {
    event.preventDefault();
    setEstadoMozos("");

    if (isCreatorMode || !isSupabaseConfigured) {
      setEstadoMozos("Para invitar mozos hace falta entrar con una cuenta real conectada a Supabase.");
      return;
    }

    if (!currentCompany?.id || !currentRestaurant?.id) {
      setEstadoMozos("No encontramos la sucursal activa para sumar un mozo.");
      return;
    }

    setLoadingWaiters(true);

    try {
      const created = await createWaiterInvitation(currentCompany.id, user?.id || "", {
        branchId: currentRestaurant.id,
        fullName: waiterForm.fullName,
        email: waiterForm.email,
        waiterPermissions: normalizeWaiterPermissions(
          waiterForm.waiterPermissions,
          DEFAULT_WAITER_PERMISSIONS
        ),
      });
      setWaiterForm(createInitialWaiterForm());
      await reloadWaiters();
      setEstadoMozos(
        created.invitation_email_sent === false
          ? created.invitation_email_error ||
              "El mozo quedó invitado, pero no se pudo enviar el correo de acceso."
          : "Mozo agregado y correo de acceso enviado correctamente."
      );
    } catch (error) {
      setEstadoMozos(error.message || "No se pudo agregar el mozo.");
    } finally {
      setLoadingWaiters(false);
    }
  };

  const handleToggleWaiter = async (waiter) => {
    setEstadoMozos("");
    setLoadingWaiters(true);

    try {
      await updateWaiterBranchAccess(waiter.branchId, waiter.userId, !waiter.branchAccessActive);
      await reloadWaiters();
      setEstadoMozos(
        waiter.branchAccessActive
          ? "Mozo pausado para esta sucursal."
          : "Mozo activado para operar en esta sucursal."
      );
    } catch (error) {
      setEstadoMozos(error.message || "No se pudo cambiar el estado del mozo.");
    } finally {
      setLoadingWaiters(false);
    }
  };

  const handleCancelWaiterInvitation = async (invitation) => {
    setEstadoMozos("");

    if (!currentCompany?.id) {
      setEstadoMozos("No encontramos la empresa activa para cancelar la invitacion.");
      return;
    }

    setLoadingWaiters(true);

    try {
      await cancelInvitation(currentCompany.id, invitation.id);
      await reloadWaiters();
      setEstadoMozos("Invitacion de mozo cancelada.");
    } catch (error) {
      setEstadoMozos(error.message || "No se pudo cancelar la invitacion del mozo.");
    } finally {
      setLoadingWaiters(false);
    }
  };

  const handleRemoveWaiter = async (waiter) => {
    setEstadoMozos("");

    if (!currentCompany?.id) {
      setEstadoMozos("No encontramos la empresa activa para eliminar el mozo.");
      return;
    }

    const confirmed = window.confirm(
      `Vas a eliminar a ${waiter.profileFullName || waiter.userId} del equipo de mozos. El usuario dejara de operar en la empresa.`
    );

    if (!confirmed) {
      return;
    }

    setLoadingWaiters(true);

    try {
      await deactivateCompanyUser(currentCompany.id, waiter.userId);
      await reloadWaiters();
      setExpandedWaiterId((current) => (current === waiter.userId ? "" : current));
      setEstadoMozos("Mozo eliminado del equipo correctamente.");
    } catch (error) {
      setEstadoMozos(error.message || "No se pudo eliminar el mozo.");
    } finally {
      setLoadingWaiters(false);
    }
  };

  const handleCopyInvitationLink = async (invitation) => {
    setEstadoMozos("");

    try {
      const registrationUrl = buildInvitationRegistrationUrl(invitation);
      await copyTextToClipboard(registrationUrl);
      setEstadoMozos("Link de registro copiado. El mozo ya puede crear la cuenta desde ahi.");
    } catch (error) {
      setEstadoMozos(error.message || "No se pudo copiar el link de registro.");
    }
  };

  const handleWaiterFormPermissionChange = (permissionKey, checked) => {
    setWaiterForm((prev) => ({
      ...prev,
      waiterPermissions: {
        ...prev.waiterPermissions,
        [permissionKey]: checked,
      },
    }));
  };

  const handleDraftPermissionChange = (userId, permissionKey, checked) => {
    setWaiterPermissionDrafts((prev) => ({
      ...prev,
      [userId]: {
        ...normalizeWaiterPermissions(prev[userId], DEFAULT_WAITER_PERMISSIONS),
        [permissionKey]: checked,
      },
    }));
  };

  const handleSaveWaiterPermissions = async (waiter) => {
    setEstadoMozos("");
    setLoadingWaiters(true);

    try {
      await updateWaiterBranchAccess(waiter.branchId, waiter.userId, {
        active: waiter.branchAccessActive,
        waiterPermissions: normalizeWaiterPermissions(
          waiterPermissionDrafts[waiter.userId],
          DEFAULT_WAITER_PERMISSIONS
        ),
      });
      await reloadWaiters();
      setEstadoMozos("Los permisos operativos del mozo ya quedaron actualizados.");
    } catch (error) {
      setEstadoMozos(error.message || "No se pudieron guardar los permisos del mozo.");
    } finally {
      setLoadingWaiters(false);
    }
  };

  const updateOption = (optionId, patch) => {
    setAssistanceConfig((prev) => ({
      ...prev,
      options: prev.options.map((option) =>
        option.id === optionId ? { ...option, ...patch } : option
      ),
    }));
  };

  const removeOption = (optionId) => {
    setAssistanceConfig((prev) => ({
      ...prev,
      options: prev.options.filter((option) => option.id !== optionId),
    }));
  };

  const addOption = () => {
    const trimmedLabel = newOptionLabel.trim();

    if (!trimmedLabel) {
      return;
    }

    setAssistanceConfig((prev) => ({
      ...prev,
      options: [...prev.options, createOptionDraft(trimmedLabel)],
    }));
    setNewOptionLabel("");
  };

  const saveOptions = async () => {
    if (loadingOptions || savingOptions || optionsLoadFailed) return;
    const scopeId = optionsScopeId;
    const remote = isSupabaseConfigured && !isCreatorMode;
    setSavingOptions(true);
    setEstadoOpciones("");
    try {
      const nextConfig = remote
        ? await saveBranchWaiterAssistance(scopeId, assistanceConfig)
        : saveWaiterAssistanceConfig(scopeId, assistanceConfig);
      if (remote) {
        try { saveWaiterAssistanceConfig(scopeId, nextConfig); }
        catch (error) { console.warn("Opciones guardadas en la sucursal, sin cache local.", error); }
      }
      if (optionsScopeRef.current !== scopeId) return;
      setAssistanceConfig(nextConfig);
      setEstadoOpciones(remote
        ? "Las opciones del boton Mozo quedaron guardadas en la sucursal y disponibles en el menu QR."
        : "Opciones guardadas solo en este navegador (modo local). No se publicaron en el menu QR.");
    } catch (error) {
      if (optionsScopeRef.current === scopeId) setEstadoOpciones(error.message || "No se pudo guardar la configuracion de mozo.");
    } finally {
      if (optionsScopeRef.current === scopeId) setSavingOptions(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <div style={styles.overline}>Mozo</div>
          <h1 style={styles.title}>Salon y llamados desde QR</h1>
          <p style={styles.subtitle}>
            Desde aca gestionas el equipo de mozos de la sucursal y defines que opciones ve el
            comensal cuando toca el boton de mozo en la carta QR.
          </p>
        </div>
        <div style={styles.headerPill}>
          {currentBranch?.name || currentRestaurant?.name || "Sucursal activa"}
        </div>
      </div>

      <TabletOrderLauncher key={optionsScopeId} branchId={optionsScopeId}
        branchName={currentBranch?.name || currentRestaurant?.name}
        localMode={isCreatorMode || !isSupabaseConfigured} />
      <div style={styles.grid}>
        <section style={styles.panel}>
          <div style={styles.panelHeader}>
            <div>
              <h2 style={styles.panelTitle}>Equipo de salon</h2>
              <span style={styles.helperText}>
                Alta y activacion rapida de mozos para esta sucursal.
              </span>
            </div>
            <span style={styles.scheduleSummary}>
              {activeWaitersCount} activos · {pendingWaiterInvitations.length} pendientes
            </span>
          </div>

          {estadoMozos ? (
            <div
              style={{
                ...styles.notice,
                ...(estadoMozos.includes("No se pudo") || estadoMozos.includes("falta")
                  ? styles.noticeError
                  : styles.noticeOk),
              }}
            >
              {estadoMozos}
            </div>
          ) : null}

          <div style={styles.waiterGrid}>
            <form onSubmit={handleCreateWaiter} style={styles.waiterFormCard}>
              <div style={styles.waiterFormHeader}>
                <strong style={styles.waiterTitle}>Agregar mozo</strong>
                <span style={styles.helperText}>Alta rapida para la sucursal activa.</span>
              </div>

              <label style={styles.field}>
                <span style={styles.label}>Nombre y apellido</span>
                <input
                  type="text"
                  value={waiterForm.fullName}
                  onChange={(event) =>
                    setWaiterForm((prev) => ({ ...prev, fullName: event.target.value }))
                  }
                  style={styles.input}
                  placeholder="Ej: Juan Perez"
                  disabled={loadingWaiters}
                />
              </label>

              <label style={styles.field}>
                <span style={styles.label}>Correo</span>
                <input
                  type="email"
                  value={waiterForm.email}
                  onChange={(event) =>
                    setWaiterForm((prev) => ({ ...prev, email: event.target.value }))
                  }
                  style={styles.input}
                  placeholder="mozo@restaurante.com"
                  disabled={loadingWaiters}
                />
              </label>

              <div style={styles.permissionPanel}>
                <strong style={styles.permissionTitle}>Permisos operativos iniciales</strong>
                <div style={styles.permissionGrid}>
                  {WAITER_PERMISSION_CONFIG.map((permission) => (
                    <label key={permission.key} style={styles.permissionOption}>
                      <input
                        type="checkbox"
                        checked={Boolean(waiterForm.waiterPermissions?.[permission.key])}
                        onChange={(event) =>
                          handleWaiterFormPermissionChange(
                            permission.key,
                            event.target.checked
                          )
                        }
                        disabled={loadingWaiters}
                      />
                      <span style={styles.permissionCopy}>
                        <strong style={styles.permissionLabel}>{permission.label}</strong>
                        <span style={styles.permissionDescription}>
                          {permission.description}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <button type="submit" style={styles.primaryButton} disabled={loadingWaiters}>
                {loadingWaiters ? "Guardando..." : "Agregar mozo"}
              </button>
            </form>

            <div style={styles.waiterListCard}>
              <div style={styles.waiterFormHeader}>
                <strong style={styles.waiterTitle}>Mozos de esta sucursal</strong>
                <span style={styles.helperText}>
                  Puedes pausarlos o volver a activarlos cuando arranca el turno.
                </span>
              </div>

              <div style={styles.waiterList}>
                {branchWaiters.length ? (
                  branchWaiters.map((waiter) => (
                    <article key={waiter.userId} style={styles.waiterRow}>
                      <div style={styles.waiterIdentity}>
                        <strong style={styles.waiterName}>
                          {waiter.profileFullName || "Mozo pendiente de completar datos"}
                        </strong>
                        <span style={styles.waiterMeta}>
                          {waiter.profilePhone || "Sin telefono cargado"}
                        </span>
                        <span style={styles.waiterPermissionSummary}>
                          {buildWaiterPermissionSummary(waiter.waiterPermissions)}
                        </span>
                      </div>

                      <div style={styles.waiterActions}>
                        <span
                          style={{
                            ...styles.waiterState,
                            ...(waiter.branchAccessActive
                              ? styles.waiterStateActive
                              : styles.waiterStatePaused),
                          }}
                        >
                          {waiter.branchAccessActive ? "Activo ahora" : "Pausado"}
                        </span>
                        <button
                          type="button"
                          style={styles.secondaryButton}
                          onClick={() => handleToggleWaiter(waiter)}
                          disabled={loadingWaiters}
                        >
                          {waiter.branchAccessActive ? "Pausar" : "Activar"}
                        </button>
                        <button
                          type="button"
                          style={styles.secondaryButton}
                          onClick={() =>
                            setExpandedWaiterId((current) =>
                              current === waiter.userId ? "" : waiter.userId
                            )
                          }
                          disabled={loadingWaiters}
                        >
                          {expandedWaiterId === waiter.userId
                            ? "Ocultar permisos"
                            : "Editar permisos"}
                        </button>
                        <button
                          type="button"
                          style={styles.dangerButton}
                          onClick={() => handleRemoveWaiter(waiter)}
                          disabled={loadingWaiters}
                        >
                          Eliminar
                        </button>
                      </div>

                      {expandedWaiterId === waiter.userId ? (
                        <div style={styles.waiterPermissionEditor}>
                          <div style={styles.permissionGrid}>
                            {WAITER_PERMISSION_CONFIG.map((permission) => (
                              <label key={permission.key} style={styles.permissionOption}>
                                <input
                                  type="checkbox"
                                  checked={Boolean(
                                    waiterPermissionDrafts[waiter.userId]?.[permission.key]
                                  )}
                                  onChange={(event) =>
                                    handleDraftPermissionChange(
                                      waiter.userId,
                                      permission.key,
                                      event.target.checked
                                    )
                                  }
                                  disabled={loadingWaiters}
                                />
                                <span style={styles.permissionCopy}>
                                  <strong style={styles.permissionLabel}>
                                    {permission.label}
                                  </strong>
                                  <span style={styles.permissionDescription}>
                                    {permission.description}
                                  </span>
                                </span>
                              </label>
                            ))}
                          </div>
                          <div style={styles.permissionActions}>
                            <button
                              type="button"
                              style={styles.primaryButton}
                              onClick={() => handleSaveWaiterPermissions(waiter)}
                              disabled={loadingWaiters}
                            >
                              Guardar permisos
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </article>
                  ))
                ) : (
                  <div style={styles.waiterEmpty}>
                    Todavia no hay mozos activos en esta sucursal.
                  </div>
                )}
              </div>

              {pendingWaiterInvitations.length ? (
                <div style={styles.pendingWaitersBlock}>
                  <strong style={styles.pendingWaitersTitle}>Invitaciones pendientes</strong>
                  <div style={styles.pendingWaitersList}>
                    {pendingWaiterInvitations.map((invitation) => (
                      <div key={invitation.id} style={styles.pendingWaiterRow}>
                        <div style={styles.pendingWaiterCopy}>
                          <span style={styles.pendingWaiterName}>
                            {invitation.display_name || invitation.email}
                          </span>
                          <span style={styles.pendingWaiterEmail}>{invitation.email}</span>
                        </div>
                        <button
                          type="button"
                          style={styles.dangerButton}
                          onClick={() => handleCancelWaiterInvitation(invitation)}
                          disabled={loadingWaiters}
                        >
                          Cancelar
                        </button>
                        <button
                          type="button"
                          style={styles.secondaryButton}
                          onClick={() => handleCopyInvitationLink(invitation)}
                          disabled={loadingWaiters}
                        >
                          Copiar link
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </section>

        <section style={styles.panel}>
          <div style={styles.panelHeader}>
            <div>
              <h2 style={styles.panelTitle}>Boton Mozo en el QR</h2>
              <span style={styles.helperText}>
                Estas opciones le aparecen al comensal cuando toca el icono de mozo.
              </span>
            </div>
            <span style={styles.scheduleSummary}>
              {assistanceConfig.options.filter((option) => option.active).length} visibles
            </span>
          </div>

          {estadoOpciones ? (
            <div
              style={{
                ...styles.notice,
                ...(estadoOpciones.includes("No se pudo")
                  ? styles.noticeError
                  : styles.noticeOk),
              }}
            >
              {estadoOpciones}
            </div>
          ) : null}

          {loadingOptions && <p role="status">Cargando opciones de la sucursal...</p>}
          {optionsLoadFailed && (
            <button type="button" style={styles.secondaryButton} onClick={() => setOptionsReload((value) => value + 1)}>
              Reintentar carga
            </button>
          )}
          <fieldset disabled={loadingOptions || savingOptions || optionsLoadFailed} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
          <div style={styles.optionList}>
            {assistanceConfig.options.map((option) => (
              <article key={option.id} style={styles.optionRow}>
                <label style={styles.optionToggle}>
                  <input
                    type="checkbox"
                    checked={option.active}
                    onChange={(event) =>
                      updateOption(option.id, { active: event.target.checked })
                    }
                  />
                  <span>Visible</span>
                </label>

                <input
                  type="text"
                  value={option.label}
                  onChange={(event) =>
                    updateOption(option.id, { label: event.target.value })
                  }
                  style={styles.input}
                />

                <button
                  type="button"
                  style={styles.dangerButton}
                  onClick={() => removeOption(option.id)}
                >
                  Quitar
                </button>
              </article>
            ))}
          </div>

          <div style={styles.addOptionRow}>
            <input
              type="text"
              value={newOptionLabel}
              onChange={(event) => setNewOptionLabel(event.target.value)}
              style={styles.input}
              placeholder="Ej: Hielo, panera, otra salsa..."
            />
            <button type="button" style={styles.secondaryButton} onClick={addOption}>
              Agregar opcion
            </button>
          </div>

          <div style={styles.optionCard}>
            <label style={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={assistanceConfig.allowCustomText}
                onChange={(event) =>
                  setAssistanceConfig((prev) => ({
                    ...prev,
                    allowCustomText: event.target.checked,
                  }))
                }
              />
              <span>Permitir texto libre del comensal</span>
            </label>

            <div style={styles.optionGrid}>
              <label style={styles.field}>
                <span style={styles.label}>Etiqueta del campo</span>
                <input
                  type="text"
                  value={assistanceConfig.customTextLabel}
                  onChange={(event) =>
                    setAssistanceConfig((prev) => ({
                      ...prev,
                      customTextLabel: event.target.value,
                    }))
                  }
                  style={styles.input}
                />
              </label>

              <label style={styles.field}>
                <span style={styles.label}>Placeholder</span>
                <input
                  type="text"
                  value={assistanceConfig.customTextPlaceholder}
                  onChange={(event) =>
                    setAssistanceConfig((prev) => ({
                      ...prev,
                      customTextPlaceholder: event.target.value,
                    }))
                  }
                  style={styles.input}
                />
              </label>
            </div>
          </div>

          <div style={styles.previewCard}>
            <strong style={styles.waiterTitle}>Vista previa QR</strong>
            <div style={styles.previewOptions}>
              {assistanceConfig.options
                .filter((option) => option.active)
                .map((option) => (
                  <span key={option.id} style={styles.previewChip}>
                    {option.label}
                  </span>
                ))}
            </div>
            {assistanceConfig.allowCustomText ? (
              <div style={styles.previewTextarea}>
                {assistanceConfig.customTextLabel}: {assistanceConfig.customTextPlaceholder}
              </div>
            ) : null}
          </div>

          <div style={styles.formInlineActions}>
            <button type="button" style={styles.primaryButton} onClick={saveOptions}>
              {savingOptions ? "Guardando..." : "Guardar opciones del QR"}
            </button>
          </div>
          </fieldset>
        </section>
      </div>
    </div>
  );
}

const styles = {
  page: {
    display: "grid",
    gap: "24px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    gap: "16px",
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  overline: {
    display: "inline-flex",
    alignItems: "center",
    padding: "8px 12px",
    borderRadius: "999px",
    backgroundColor: "#ecfeff",
    color: "#155e75",
    fontWeight: 800,
    fontSize: "12px",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    marginBottom: "12px",
  },
  title: {
    margin: 0,
    color: "#0f172a",
    fontSize: "32px",
  },
  subtitle: {
    margin: "10px 0 0",
    color: "#475569",
    lineHeight: 1.6,
    maxWidth: "760px",
  },
  headerPill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "10px 14px",
    borderRadius: "999px",
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    fontWeight: 700,
  },
  grid: {
    display: "grid",
    gap: "18px",
  },
  panel: {
    border: "1px solid #dbe2ea",
    borderRadius: "18px",
    padding: "20px",
    backgroundColor: "#ffffff",
    display: "grid",
    gap: "16px",
  },
  panelHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
    alignItems: "flex-start",
  },
  panelTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: "22px",
  },
  helperText: {
    color: "#64748b",
    lineHeight: 1.5,
    fontSize: "14px",
  },
  scheduleSummary: {
    color: "#334155",
    fontWeight: 700,
    fontSize: "13px",
  },
  notice: {
    borderRadius: "14px",
    padding: "14px 16px",
    fontWeight: 600,
  },
  noticeOk: {
    backgroundColor: "#ecfdf5",
    border: "1px solid #86efac",
    color: "#166534",
  },
  noticeError: {
    backgroundColor: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#b91c1c",
  },
  waiterGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(260px, 360px) minmax(0, 1fr)",
    gap: "18px",
    alignItems: "start",
  },
  waiterFormCard: {
    border: "1px solid #dbe2ea",
    borderRadius: "16px",
    padding: "18px",
    backgroundColor: "#fbfdff",
    display: "grid",
    gap: "14px",
  },
  waiterListCard: {
    border: "1px solid #dbe2ea",
    borderRadius: "16px",
    padding: "18px",
    backgroundColor: "#ffffff",
    display: "grid",
    gap: "16px",
  },
  waiterFormHeader: {
    display: "grid",
    gap: "4px",
  },
  waiterTitle: {
    color: "#0f172a",
    fontSize: "16px",
  },
  field: {
    display: "grid",
    gap: "8px",
  },
  label: {
    color: "#475569",
    fontSize: "13px",
    fontWeight: 700,
  },
  input: {
    width: "100%",
    borderRadius: "12px",
    border: "1px solid #cbd5e1",
    padding: "11px 12px",
    boxSizing: "border-box",
    backgroundColor: "#ffffff",
    color: "#0f172a",
  },
  permissionPanel: {
    display: "grid",
    gap: "12px",
    padding: "14px",
    borderRadius: "14px",
    backgroundColor: "#ffffff",
    border: "1px solid #dbe2ea",
  },
  permissionTitle: {
    color: "#0f172a",
    fontSize: "14px",
  },
  permissionGrid: {
    display: "grid",
    gap: "10px",
  },
  permissionOption: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    color: "#334155",
  },
  permissionCopy: {
    display: "grid",
    gap: "4px",
  },
  permissionLabel: {
    color: "#0f172a",
    fontSize: "13px",
  },
  permissionDescription: {
    color: "#64748b",
    fontSize: "12px",
    lineHeight: 1.45,
  },
  primaryButton: {
    border: "none",
    borderRadius: "12px",
    padding: "12px 16px",
    backgroundColor: "#0f172a",
    color: "#ffffff",
    fontWeight: 700,
    cursor: "pointer",
  },
  secondaryButton: {
    border: "1px solid #cbd5e1",
    borderRadius: "12px",
    padding: "10px 14px",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    fontWeight: 700,
    cursor: "pointer",
  },
  dangerButton: {
    border: "1px solid #fecaca",
    borderRadius: "12px",
    padding: "10px 14px",
    backgroundColor: "#fef2f2",
    color: "#b91c1c",
    fontWeight: 700,
    cursor: "pointer",
  },
  waiterList: {
    display: "grid",
    gap: "10px",
  },
  waiterRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "14px",
    padding: "14px 16px",
    borderRadius: "14px",
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    flexWrap: "wrap",
  },
  waiterIdentity: {
    display: "grid",
    gap: "4px",
  },
  waiterName: {
    color: "#0f172a",
  },
  waiterMeta: {
    color: "#64748b",
    fontSize: "13px",
  },
  waiterPermissionSummary: {
    color: "#334155",
    fontSize: "12px",
    lineHeight: 1.5,
  },
  waiterActions: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
  },
  waiterPermissionEditor: {
    width: "100%",
    display: "grid",
    gap: "12px",
    paddingTop: "6px",
    borderTop: "1px solid #e2e8f0",
  },
  permissionActions: {
    display: "flex",
    justifyContent: "flex-end",
  },
  waiterState: {
    display: "inline-flex",
    alignItems: "center",
    padding: "7px 12px",
    borderRadius: "999px",
    fontWeight: 700,
    fontSize: "12px",
  },
  waiterStateActive: {
    backgroundColor: "#dcfce7",
    color: "#166534",
  },
  waiterStatePaused: {
    backgroundColor: "#fff7ed",
    color: "#c2410c",
  },
  waiterEmpty: {
    padding: "18px",
    borderRadius: "14px",
    backgroundColor: "#f8fafc",
    border: "1px dashed #cbd5e1",
    color: "#64748b",
  },
  pendingWaitersBlock: {
    display: "grid",
    gap: "10px",
    paddingTop: "4px",
  },
  pendingWaitersTitle: {
    color: "#334155",
  },
  pendingWaitersList: {
    display: "grid",
    gap: "8px",
  },
  pendingWaiterRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    padding: "12px 14px",
    borderRadius: "12px",
    backgroundColor: "#fff7ed",
    border: "1px solid #fed7aa",
    color: "#9a3412",
    flexWrap: "wrap",
  },
  pendingWaiterCopy: {
    display: "grid",
    gap: "4px",
  },
  pendingWaiterName: {
    fontWeight: 700,
  },
  pendingWaiterEmail: {
    fontSize: "13px",
  },
  optionList: {
    display: "grid",
    gap: "10px",
  },
  optionRow: {
    display: "grid",
    gridTemplateColumns: "110px minmax(0, 1fr) auto",
    gap: "12px",
    alignItems: "center",
    padding: "14px",
    borderRadius: "14px",
    border: "1px solid #e2e8f0",
    backgroundColor: "#f8fafc",
  },
  optionToggle: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: "#334155",
    fontWeight: 700,
    fontSize: "13px",
  },
  addOptionRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gap: "12px",
  },
  optionCard: {
    display: "grid",
    gap: "14px",
    padding: "16px",
    borderRadius: "16px",
    border: "1px solid #e2e8f0",
    backgroundColor: "#ffffff",
  },
  optionGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "12px",
  },
  checkboxRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    color: "#0f172a",
    fontWeight: 700,
  },
  previewCard: {
    display: "grid",
    gap: "12px",
    padding: "16px",
    borderRadius: "16px",
    border: "1px solid #dbeafe",
    backgroundColor: "#f8fbff",
  },
  previewOptions: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
  },
  previewChip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "8px 12px",
    borderRadius: "999px",
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    fontWeight: 700,
    fontSize: "13px",
  },
  previewTextarea: {
    padding: "12px 14px",
    borderRadius: "12px",
    border: "1px dashed #93c5fd",
    color: "#475569",
    backgroundColor: "#ffffff",
  },
  formInlineActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    flexWrap: "wrap",
  },
};

export default MozoAdmin;
